import classNames from 'classnames';
import React, { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Container } from 'src/components/layouts/Container/Container';
import CarrierAnalyticsToolbar, {
  analyticsRangeToDates,
  initialAnalyticsRange,
} from 'src/components/layouts/CarrierAnalyticsToolbar/CarrierAnalyticsToolbar';

import StackedDoubleInfoBox from 'src/components/ui/StackedDoubleInfoBox/StackedDoubleInfoBox';
import RowStatsBox from 'src/components/ui/FourStatsBox/RowStatsBox';
import DashboardKeyValueRankingKpiCard from 'src/components/cardLayouts/DashboardKeyValueRankingKpiCard/DashboardKeyValueRankingKpiCard';
import KeyValueListWithDiagramKpiCard from 'src/components/cardLayouts/KeyValueListWithDiagramKpiCard/KeyValueListWithDiagramKpiCard';
import DashboardBarChartDiagramKpiCard from 'src/components/cardLayouts/DashboardBarChartDiagramKpiCard/DashboardBarChartDiagramKpiCard';
import { useTranslation } from 'react-i18next';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';
import { DashboardKpi, useDashboardKpiQuery } from 'src/services/kpi';
import {
  autoGranularity,
  bucketShipmentsSeries,
  progressToTrend,
  toShipmentsBarChartKpi,
  useCarrierAnalytics,
} from 'src/services/carrierKpi';
import { buildShareKpiRows } from './CarrierAnalytics';
import styles from './styles/CarrierAnalytics.module.scss';

function CarrierAnalyticsDetail() {
  const { t } = useTranslation();
  const { name } = useParams();
  const [rangeState, setRangeState] = useState(initialAnalyticsRange);
  const dateRange = analyticsRangeToDates(rangeState);
  const { carriers, kpiDateBounds, isLoading } = useCarrierAnalytics(dateRange);
  const { data: dashboardData } = useDashboardKpiQuery('main.dashboard');
  const dashboardKpis = dashboardData as DashboardKpi[] | undefined;

  const carrier = carriers.find(entry => entry.code === name);

  const topProducts = dashboardKpis?.find(kpi => kpi.kpi_type === 'dashboard_key_value_ranking');
  const destinations = dashboardKpis?.find(kpi => kpi.kpi_type === 'key_value_list_with_diagram');

  const chart =
    carrier &&
    toShipmentsBarChartKpi(
      bucketShipmentsSeries(carrier.data.shipmentsPerDay, autoGranularity(dateRange, carrier.data.shipmentsPerDay)),
      t('carrier_analytics.delivered_packages'),
      t('carrier_analytics.delivered_packages'),
    );

  const shareStats = carrier ? buildShareKpiRows(carrier.data, t).map(row => ({ ...row, score: `${row.score}` })) : [];

  const infoStats = carrier
    ? [
        carrier.data.avgPackageWeight && {
          label: t('carrier_analytics.avg_package_weight'),
          score: `${Math.round(carrier.data.avgPackageWeight.value * 100) / 100} kg`,
          trend: progressToTrend(carrier.data.avgPackageWeight),
        },
        carrier.data.investigations && {
          label: t('carrier_analytics.investigations'),
          score: `${carrier.data.investigations.value}`,
          trend: progressToTrend(carrier.data.investigations),
        },
      ].filter((stat): stat is { label: string; score: string; trend: number } => Boolean(stat))
    : [];

  if (!isLoading && !carrier) {
    return <Navigate to="/carrier-analytics/dashboard" replace />;
  }

  return (
    <Container
      title={`${t('navbar.carrier_analytics')} / ${carrier?.name ?? 'Dashboard'}`}
      className={classNames(styles.root, 'relative py-32 flex-1')}>
      <HeadlineWithButtons headline={carrier?.name ?? t('navbar.carrier_analytics')} />
      <CarrierAnalyticsToolbar value={rangeState} onChange={setRangeState} kpiDateBounds={kpiDateBounds} />

      {carrier && (
        <>
          <RowStatsBox layout="row" unit="%" data={shareStats} />

          <div className="pt-20 grid grid-cols-1 lg:grid-cols-12 gap-20">
            <div className={classNames('col-span-1', infoStats.length > 0 ? 'lg:col-span-8' : 'lg:col-span-12')}>
              {chart && <DashboardBarChartDiagramKpiCard title={chart.title} data={chart} />}
            </div>
            {infoStats.length > 0 && (
              <div className="col-span-1 lg:col-span-4">
                <StackedDoubleInfoBox className="h-full" data={infoStats} />
              </div>
            )}
          </div>
        </>
      )}

      {(topProducts || destinations) && (
        <div className="pt-20 pb-32 grid grid-cols-1 lg:grid-cols-12 gap-20">
          {topProducts && topProducts.kpi_type === 'dashboard_key_value_ranking' && (
            <div className="col-span-1 lg:col-span-6">
              <DashboardKeyValueRankingKpiCard data={topProducts} />
            </div>
          )}
          {destinations && destinations.kpi_type === 'key_value_list_with_diagram' && (
            <div className="col-span-1 lg:col-span-6">
              <KeyValueListWithDiagramKpiCard data={destinations} />
            </div>
          )}
        </div>
      )}
    </Container>
  );
}

export default CarrierAnalyticsDetail;
